import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import ToastAlert, { ToastCloseIcon } from "../ToastAlert/ToastAlert.jsx";
import PageBanner from "../PageBanner/PageBanner.jsx";
import ContainerBleed from "../Container/ContainerBleed.jsx";
import ContainerFull from "../Container/ContainerFull.jsx";
import {
  AddClientDialog,
  UpdateClientDialog,
  SecretRevealDialog,
  RevokeConfirmDialog,
  EmailSecretDialog,
} from "./APIKeyDialogs.jsx";
import { APIKeyPoliciesDialog } from "./APIKeyPolicies.jsx";
const API_BASE = "/api/ApiClient";
const REQUEST_TIMEOUT_MS = 20000;
const MAX_ERROR_LENGTH = 300;
const sanitizeMessage = value => {
  if (value === null || value === undefined) return "";
  let s = String(value);

  s = s.replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, " ");

  s = s.replace(/\s+/g, " ").trim();
  if (s.length > MAX_ERROR_LENGTH) {
    s = `${s.slice(0, MAX_ERROR_LENGTH - 1)}\u2026`;
  }
  return s;
};
const readServerError = async response => {
  let rawBody = "";
  try {
    rawBody = await response.text();
  } catch (e) {
    return "";
  }
  let data = null;
  try {
    data = rawBody ? JSON.parse(rawBody) : null;
  } catch (e) {
    /* fall through */
  }
  if (data) {
    const modelState = data.modelState || data.ModelState;
    if (modelState) {
      const messages = Object.keys(modelState)
        .reduce((acc, key) => acc.concat(modelState[key]), [])
        .filter(Boolean);
      if (messages.length) return sanitizeMessage(messages.join(" "));
    }
    const message =
      data.message ||
      data.Message ||
      data.exceptionMessage ||
      data.ExceptionMessage;
    if (message) return sanitizeMessage(message);
  }

  if (rawBody && !/<html[\s>]/i.test(rawBody)) {
    return sanitizeMessage(rawBody);
  }
  return "";
};
const makeApiError = (category, message, status) => {
  const err = new Error(message);
  err.category = category;
  if (typeof status === "number") err.status = status;
  return err;
};
const messageForStatus = (status, detail) => {
  if (status === 401 || status === 403) {
    return "You do not have permission to perform this action, or your session has expired. Reload the page and sign in again.";
  }
  if (status === 404) {
    return detail || "The requested item could not be found.";
  }
  if (status === 409) {
    return (
      detail ||
      "That change conflicts with the current state. Refresh and try again."
    );
  }
  if (status >= 400 && status < 500) {
    return detail || `The server rejected the request (HTTP ${status}).`;
  }
  if (status >= 500) {
    return "The server ran into an error. Please try again in a moment.";
  }
  return detail || `Unexpected response (HTTP ${status}).`;
};
const showToast = (variant, title, message) => {
  toast(
    <ToastAlert style={variant} title={title} description={message} />,
    {
      /* Base .st-toast-alert prevents the toast from falling back to
       * react-toastify's default styling (which triggers the global
       * body:not(.theme-vu) h4 { 2rem } rule and wraps short titles).
       *
       * The extra .api-key-manager__toast scope re-skins the success
       * variant's green icon gradient to VU blue — kept scoped so we
       * don't repaint the global success toast used elsewhere. */
      className: "st-toast-alert api-key-manager__toast",
      closeButton: <ToastCloseIcon />,
    },
  );
};
const formatDate = value => {
  if (!value) return "Never";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "\u2014";
  return d.toISOString().slice(0, 10);
};
const clientStatus = c => {
  if (c.isActive === false) return "revoked";
  if (c.expiresOn && new Date(c.expiresOn) < new Date()) return "expired";
  return "active";
};
const statusLabel = {
  active: "Active",
  expired: "Expired",
  revoked: "Revoked",
};

/* Client-side page size for the list table. Matches the enterprise
 * default (10 rows / page) and gives predictable vertical rhythm. */
const CLIENTS_PER_PAGE = 10;

/* ==================================================================
 * Pagination
 * -----------------------------------------------------------------
 * Local numbered pager with Previous / Next controls. Renders a
 * sliding window of at most 5 page numbers around the current page.
 * Returns null when there is only one page so it doesn't add
 * whitespace when the list is short.
 * ================================================================== */
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) pages.push(i);

  const go = p => {
    if (p >= 1 && p <= totalPages && p !== currentPage) onPageChange(p);
  };

  return (
    <nav
      className="api-key-manager__pagination"
      role="navigation"
      aria-label="Client list pages"
    >
      <button
        type="button"
        className="btn api-key-manager__page-btn api-key-manager__page-btn--nav"
        onClick={() => go(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
        title="Previous page"
      >
        <svg
          className="api-key-manager__page-btn-icon"
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M12.5 4L6.5 10l6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <ul className="api-key-manager__pages">
        {start > 1 && (
          <li className="api-key-manager__pages-ellipsis" aria-hidden="true">
            …
          </li>
        )}
        {pages.map(p => (
          <li key={p}>
            <button
              type="button"
              className={`btn api-key-manager__page-btn${p === currentPage ? " is-current" : ""}`}
              onClick={() => go(p)}
              aria-current={p === currentPage ? "page" : undefined}
              aria-label={`Go to page ${p}`}
            >
              {p}
            </button>
          </li>
        ))}
        {end < totalPages && (
          <li className="api-key-manager__pages-ellipsis" aria-hidden="true">
            …
          </li>
        )}
      </ul>
      <button
        type="button"
        className="btn api-key-manager__page-btn api-key-manager__page-btn--nav"
        onClick={() => go(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
        title="Next page"
      >
        <svg
          className="api-key-manager__page-btn-icon"
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M7.5 4l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </nav>
  );
};

/* ==================================================================
 * CopyButton
 * -----------------------------------------------------------------
 * Small icon-only button that copies `value` to the clipboard and
 * flashes a check icon for 1.5s. Exported so the shared control can
 * be reused on the API History URL column. Uses the async Clipboard
 * API when available with a synchronous execCommand fallback for
 * older shells embedded inside Sitecore.
 * ================================================================== */
export const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleCopy = async e => {
    e.stopPropagation();
    if (!value) return;
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch (err) {
      ok = false;
    }
    if (!ok) return;
    setCopied(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 1500);
  };

  const tip = copied ? "Copied" : label || "Copy";
  return (
    <button
      type="button"
      className={`api-key-manager__copy-btn${copied ? " is-copied" : ""}`}
      onClick={handleCopy}
      disabled={!value}
      aria-label={tip}
      title={tip}
    >
      {copied ? (
        <svg
          className="api-key-manager__copy-btn-icon"
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M4 10.5l4 4 8-9"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          className="api-key-manager__copy-btn-icon"
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
        >
          <rect
            x="4"
            y="6"
            width="10"
            height="10"
            rx="1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M7 4h7a1.5 1.5 0 0 1 1.5 1.5v9"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
};

const APIKeyManager = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null);

  const [selected, setSelected] = useState(null);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [busyId, setBusyId] = useState(null);
  /* Currently-open policies dialog target, if any. Kept independent
   * of `dialog` so admins can drill into a client's policies without
   * disturbing any in-flight Add/Update/Revoke on the main list. */
  const [policiesFor, setPoliciesFor] = useState(null);

  /* Table UX state:
   *  - page : current 1-based page index (client-side slicing).
   * Client IDs render as plain full-length text — no reveal / copy
   * affordance, per admin UX policy for this page.
   */
  const [page, setPage] = useState(1);

  const totalPages = Math.max(
    1,
    Math.ceil(clients.length / CLIENTS_PER_PAGE),
  );
  /* Snap the current page into range whenever the list shrinks (e.g.
   * after a revoke) so the table doesn't render empty. */
  const currentPage = Math.min(page, totalPages);
  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const pageStart = (currentPage - 1) * CLIENTS_PER_PAGE;
  const pageClients = clients.slice(pageStart, pageStart + CLIENTS_PER_PAGE);

  const abortersRef = useRef(new Set());
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
      abortersRef.current.forEach(c => {
        try {
          c.abort();
        } catch (e) {
          /* noop */
        }
      });
      abortersRef.current.clear();

      setCreatedSecret(null);
    },
    [],
  );

  const apiRequest = useCallback(async (url, options = {}) => {
    const controller = new AbortController();
    abortersRef.current.add(controller);
    const timeoutId = setTimeout(() => {
      controller.__timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    const hasBody = options.body !== undefined && options.body !== null;
    const headers = {
      Accept: "application/json",
      ...(hasBody
        ? {
            "Content-Type": "application/json",
          }
        : {}),
      ...(options.headers || {}),
    };
    let response;
    try {
      response = await fetch(url, {
        credentials: "same-origin",
        ...options,
        headers,
        signal: controller.signal,
      });
    } catch (err) {
      if (err && err.name === "AbortError") {
        if (controller.__timedOut) {
          throw makeApiError(
            "timeout",
            "The request timed out. Please check your connection and try again.",
          );
        }
        throw makeApiError("aborted", "Request cancelled.");
      }

      throw makeApiError(
        "network",
        "Could not reach the server. Please check your connection and try again.",
      );
    } finally {
      clearTimeout(timeoutId);
      abortersRef.current.delete(controller);
    }
    if (!response.ok) {
      const detail = await readServerError(response);
      const status = response.status;
      let category = "server";
      if (status === 401 || status === 403) category = "auth";
      else if (status >= 400 && status < 500) category = "validation";
      throw makeApiError(category, messageForStatus(status, detail), status);
    }
    if (response.status === 204) return null;
    let text = "";
    try {
      text = await response.text();
    } catch (e) {
      throw makeApiError(
        "parse",
        "The server returned an unreadable response.",
      );
    }
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      throw makeApiError(
        "parse",
        "The server returned an unexpected response. You may have been signed out ΓÇö try reloading the page.",
      );
    }
  }, []);

  const handleError = (title, err) => {
    if (!err || err.category === "aborted") return;
    if (!isMountedRef.current) return;
    showToast("error", title, err.message || "Something went wrong.");
  };
  const fetchClients = useCallback(async () => {
    if (isMountedRef.current) setLoading(true);
    try {
      const data = await apiRequest(API_BASE, {
        method: "GET",
      });
      if (!isMountedRef.current) return;

      const list = (Array.isArray(data) ? data : []).map(c => ({
        id: c.id ?? c.Id,
        clientId: c.clientId ?? c.ClientId,
        clientName: c.clientName ?? c.ClientName,
        notes: c.notes ?? c.Notes,
        isActive: c.isActive ?? c.IsActive,
        createdOn: c.createdOn ?? c.CreatedOn,
        expiresOn: c.expiresOn ?? c.ExpiresOn,
      }));
      setClients(list);
    } catch (err) {
      handleError("Could not load clients", err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [apiRequest]);
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);
  const handleCreate = async form => {
    try {
      const data = await apiRequest(API_BASE, {
        method: "POST",
        body: JSON.stringify({
          clientName: form.clientName,
          notes: form.notes || null,
          isActive: true,
          expiresOn: form.expiresOn || null,
        }),
      });
      if (!isMountedRef.current) return;
      // Response shape: { Id: { ClientId, ClientSecret, ... }, Message }
      const payload =
        (data && (data.id ?? data.Id)) && typeof (data.id ?? data.Id) === "object"
          ? data.id ?? data.Id
          : data;
      const clientId =
        (payload && (payload.clientId ?? payload.ClientId)) || "";
      const clientSecret =
        (payload && (payload.clientSecret ?? payload.ClientSecret)) || "";
      setCreatedSecret({
        name: form.clientName,
        clientId,
        clientSecret,
      });
      setDialog("secret");
      showToast(
        "success",
        "Client created",
        `${form.clientName} was created. Copy the secret now. It will not be shown again.`,
      );
      fetchClients();
    } catch (err) {
      handleError("Could not create client", err);
      throw err;
    }
  };
  const handleUpdate = async (id, form) => {
    if (isMountedRef.current) setBusyId(id);
    try {
      await apiRequest(`${API_BASE}/${encodeURIComponent(id)}`, {
        method: "PUT",
        body: JSON.stringify({
          clientName: form.clientName,
          notes: form.notes || null,
          isActive: form.isActive,
          expiresOn: form.expiresOn || null,
        }),
      });
      if (!isMountedRef.current) return;
      showToast("success", "Client updated", `${form.clientName} was saved.`);
      setDialog(null);
      setSelected(null);
      fetchClients();
    } catch (err) {
      handleError("Could not update client", err);
      throw err;
    } finally {
      if (isMountedRef.current) setBusyId(null);
    }
  };
  const handleRevoke = async client => {
    if (isMountedRef.current) setBusyId(client.id);
    try {
      await apiRequest(`${API_BASE}/${encodeURIComponent(client.id)}`, {
        method: "PUT",
        body: JSON.stringify({
          clientName: client.clientName,
          notes: client.notes || null,
          isActive: false,
          expiresOn: client.expiresOn || null,
        }),
      });
      if (!isMountedRef.current) return;
      showToast(
        "success",
        "Client revoked",
        `${client.clientName} can no longer authenticate.`,
      );
      setDialog(null);
      setSelected(null);
      fetchClients();
    } catch (err) {
      handleError("Could not revoke client", err);
    } finally {
      if (isMountedRef.current) setBusyId(null);
    }
  };
  const handleEmailSecret = async (client, to) => {
    if (isMountedRef.current) setBusyId(client.id);
    try {
      await apiRequest(
        `${API_BASE}/${encodeURIComponent(client.id)}/EmailSecret`,
        {
          method: "POST",
          body: JSON.stringify({ to }),
        },
      );
      if (!isMountedRef.current) return;
      showToast(
        "success",
        "Secret emailed",
        `Client secret for ${client.clientName} was sent to ${to}.`,
      );
      setDialog(null);
      setSelected(null);
    } catch (err) {
      handleError("Could not email client secret", err);
      throw err;
    } finally {
      if (isMountedRef.current) setBusyId(null);
    }
  };
  return (
    <>
      {" "}
      <ContainerBleed backgroundColor="#f2f2f2">
        {" "}
        <PageBanner
          darkText={true}
          showAccentLine={true}
          title="API Client Manager"
        />{" "}
      </ContainerBleed>{" "}
      <ContainerFull
        style={{
          paddingBlock: "4rem",
        }}
      >
        {" "}
        <div className="api-key-manager">
          {" "}
          <header className="api-key-manager__header">
            {" "}
            <div className="api-key-manager__header-text">
              {" "}
              <div
                className="api-key-manager__title"
                role="heading"
                aria-level={2}
              >
                Clients
              </div>
              <p className="api-key-manager__subtitle">
                Issue and manage credentials for external systems calling
                Virtual Underwriter APIs.
              </p>{" "}
            </div>{" "}
            <div className="api-key-manager__header-actions">
              <a
                className="api-key-manager__secondary-action"
                href="/sitecore/shell/Applications/VUApiHistory.aspx"
              >
                <span
                  className="api-key-manager__secondary-action-icon"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 16 16" width="14" height="14">
                    <circle
                      cx="8"
                      cy="8"
                      r="5.75"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 4.75 V8 L10.25 9.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                View API History
              </a>
              <button
                type="button"
                className="btn btn--primary api-key-manager__primary-action"
                onClick={() => setDialog("add")}
                disabled={loading}
              >
                <span
                  className="api-key-manager__primary-action-icon"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 16 16" width="14" height="14">
                    <path
                      d="M8 2.75 V13.25 M2.75 8 H13.25"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                Add API Client
              </button>
            </div>
          </header>{" "}
          <div className="api-key-manager__table-wrap">
            {" "}
            {loading && clients.length === 0 ? (
              <p className="api-key-manager__empty">Loading clients&hellip;</p>
            ) : clients.length === 0 ? (
              <p className="api-key-manager__empty">
                No API clients yet. Click &ldquo; Add API Client&rdquo; to
                create one.
              </p>
            ) : (
              <div className="api-key-manager__table-scroll">
                {" "}
                <table className="api-key-manager__table">
                  {" "}
                  <thead>
                    {" "}
                    <tr>
                      {" "}
                      <th scope="col">Client Name</th>{" "}
                      <th scope="col">Status</th> <th scope="col">Notes</th>{" "}
                      <th scope="col">Client ID</th>{" "}
                      <th scope="col">Created</th> <th scope="col">Expires</th>{" "}
                      <th
                        scope="col"
                        className="api-key-manager__col-actions"
                      >
                        Actions
                      </th>{" "}
                    </tr>{" "}
                  </thead>{" "}
                  <tbody>
                    {" "}
                    {pageClients.map(c => {
                      const status = clientStatus(c);
                      return (
                        <tr
                          key={c.id}
                          className={busyId === c.id ? "is-busy" : ""}
                        >
                          {" "}
                          <td data-label="Client Name">{c.clientName}</td>{" "}
                          <td data-label="Status">
                            {" "}
                            <span
                              className={`api-key-manager__badge api-key-manager__badge--${status}`}
                            >
                              {" "}
                              {statusLabel[status]}{" "}
                            </span>{" "}
                          </td>{" "}
                          <td
                            className="api-key-manager__notes-cell"
                            data-label="Notes"
                            title={c.notes || ""}
                          >
                            {c.notes ? (
                              c.notes
                            ) : (
                              <span className="api-key-manager__cell-empty">
                                {"\u2014"}
                              </span>
                            )}
                          </td>{" "}
                          <td
                            className="api-key-manager__client-id-cell"
                            data-label="Client ID"
                            title={c.clientId || ""}
                          >
                            {c.clientId ? (
                              <div className="api-key-manager__client-id-wrap">
                                <span className="api-key-manager__client-id">
                                  {c.clientId}
                                </span>
                                <CopyButton
                                  value={c.clientId}
                                  label="Copy client ID"
                                />
                              </div>
                            ) : (
                              <span className="api-key-manager__cell-empty">
                                {"\u2014"}
                              </span>
                            )}
                          </td>{" "}
                          <td data-label="Created">{formatDate(c.createdOn)}</td>{" "}
                          <td data-label="Expires">{formatDate(c.expiresOn)}</td>{" "}
                          <td
                            className="api-key-manager__col-actions"
                            data-label="Actions"
                          >
                            <span className="api-key-manager__row-actions">
                              <button
                                type="button"
                                className="api-key-manager__row-action"
                                onClick={() => {
                                  setSelected(c);
                                  setDialog("update");
                                }}
                                disabled={busyId === c.id}
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                className="api-key-manager__row-action"
                                onClick={() => setPoliciesFor(c)}
                                disabled={busyId === c.id}
                                aria-label={`Manage usage policies for ${c.clientName || "client"}`}
                              >
                                Policies
                              </button>
                              <button
                                type="button"
                                className="api-key-manager__row-action"
                                onClick={() => {
                                  setSelected(c);
                                  setDialog("email");
                                }}
                                disabled={busyId === c.id || status === "revoked"}
                                title={
                                  status === "revoked"
                                    ? "Revoked clients cannot receive credentials. Reactivate the client first."
                                    : undefined
                                }
                                aria-label={`Email client secret for ${c.clientName || "client"}`}
                              >
                                Email secret
                              </button>
                              <button
                                type="button"
                                className="api-key-manager__row-action api-key-manager__row-action--danger"
                                onClick={() => {
                                  setSelected(c);
                                  setDialog("revoke");
                                }}
                                disabled={busyId === c.id || status === "revoked"}
                                title={
                                  status === "revoked"
                                    ? "Client is already revoked."
                                    : undefined
                                }
                              >
                                Revoke
                              </button>
                            </span>
                          </td>{" "}
                        </tr>
                      );
                    })}{" "}
                  </tbody>{" "}
                </table>{" "}
              </div>
            )}
          </div>{" "}
          {clients.length > CLIENTS_PER_PAGE && (
            <div className="api-key-manager__pagination-bar">
              <div
                className="api-key-manager__page-summary"
                aria-live="polite"
              >
                Showing {pageStart + 1}–{pageStart + pageClients.length} of{" "}
                {clients.length}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>{" "}
      </ContainerFull>{" "}
      {dialog === "add" && (
        <AddClientDialog
          onSubmit={handleCreate}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "update" && selected && (
        <UpdateClientDialog
          client={selected}
          busy={busyId === selected.id}
          onSubmit={form => handleUpdate(selected.id, form)}
          onClose={() => {
            setDialog(null);
            setSelected(null);
          }}
        />
      )}
      {dialog === "secret" && createdSecret && (
        <SecretRevealDialog
          {...createdSecret}
          onClose={() => {
            setDialog(null);
            setCreatedSecret(null);
          }}
        />
      )}
      {dialog === "revoke" && selected && (
        <RevokeConfirmDialog
          client={selected}
          busy={busyId === selected.id}
          onConfirm={() => handleRevoke(selected)}
          onClose={() => {
            setDialog(null);
            setSelected(null);
          }}
        />
      )}
      {dialog === "email" && selected && (
        <EmailSecretDialog
          client={selected}
          busy={busyId === selected.id}
          onSubmit={to => handleEmailSecret(selected, to)}
          onClose={() => {
            setDialog(null);
            setSelected(null);
          }}
        />
      )}
      {policiesFor && (
        <APIKeyPoliciesDialog
          client={policiesFor}
          apiRequest={apiRequest}
          showToast={showToast}
          onClose={() => setPoliciesFor(null)}
        />
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar
        closeOnClick
      />{" "}
    </>
  );
};
export default APIKeyManager;
